import React, { useMemo } from 'react';
import styled from 'styled-components';

import useUser from 'core/hooks/useUser';

import { EXTERNAL_LINKS } from 'core/api/constants';
import { Box, Flex, Link, RouterLink, Logo } from 'core/ui/atoms';
import LogoutButton from 'core/ui/components/LogoutButton';

function Footer(props) {
  const { userCan } = useUser();
  const canLogOut = userCan.logOut();

  return useMemo(() => {
    return (
      <FooterContainer>
        <MethodologyLogo style={{ fontSize: '2em' }} />
        <StyledFooter {...props}>
          <Left>
            <LinkText href={EXTERNAL_LINKS.ourStory} target='_blank'>Our Story</LinkText>
            <LinkText href={EXTERNAL_LINKS.menu} target='_blank'>See Menu</LinkText>
            <LinkText href={EXTERNAL_LINKS.giftCards} target='_blank'>Gift Cards</LinkText>
            <LinkText href={EXTERNAL_LINKS.blog} target='_blank'>Blog</LinkText>
          </Left>
          <Right>
            <LinkText href={EXTERNAL_LINKS.faq} target='_blank'>FAQs</LinkText>
            <LinkText href={EXTERNAL_LINKS.careers} target='_blank'>Careers</LinkText>
            <LinkText href={EXTERNAL_LINKS.contact} target='_blank'>Contact Us</LinkText>
            {
              canLogOut ?
                <LinkButton reload={props.logoutReload || false} btn-unstyled key="nav-sign-out">Log out</LinkButton>
                :
                <RouterLink to='/sign-in'>Log In/Sign Up</RouterLink>
            }
          </Right>
        </StyledFooter>
        <BottomFooter>
          <Flex direction='row' justifyContent='space-between' alignItems='flex-start'>
            <LinkText href={EXTERNAL_LINKS.privacyPolicy} target='_blank'>Privacy Policy</LinkText>
            <LinkText href={EXTERNAL_LINKS.termsAndConditions} target='_blank'>Terms and Conditions</LinkText>
          </Flex>
          <LinkText href={EXTERNAL_LINKS.accessibility} target='_blank'>Accessibility</LinkText>
        </BottomFooter>
      </FooterContainer>
    );
  }, [props, canLogOut]);
}

const FooterContainer = styled(Box)`
  background: ${(p) => p.theme.colors.creamWhite};
`;

const LinkText = styled(Link)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${(p) => p.theme.colors.primaryV2};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  text-decoration: auto;
`;

const LinkButton = styled(LogoutButton)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${(p) => p.theme.colors.primaryV2};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
`;

const MethodologyLogo = styled(Logo)`
  padding-left: 30px;
  padding-top: 30px;
`;

const StyledFooter = styled.footer`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  padding: 2rem 2rem 0;
  background: ${(p) => p.theme.colors.creamWhite};

  a, button {
    display: block;
    margin-bottom: 1.5rem;
    background: transparent;
    line-height: ${(p) => p.theme.typography.lineHeight.base};
    border: none;
    padding: 0;
    text-decoration: none;
  }

  ${(p) => p.theme.max('sm')`
    grid-template-columns: 53%;
    padding: 2rem 1rem 0;
  `}
`;

const Section = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
`;

const Left = styled(Section)`
  grid-column: 1;
`;

const Right = styled(Section)`
  grid-column: 2;
`;

const BottomFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 2rem;
  background: ${(p) => p.theme.colors.creamWhite};
  border-top: 1px solid ${(p) => p.theme.colors.borderLight || '#E5E7EB'};

  > a {
    color: ${(p) => p.theme.typography.colors.primary};
    text-decoration: none;
  }

  ${(p) => p.theme.max('sm')`
    padding: 1rem;
  `}
`;

export default Footer;
